import type { CustomFilter } from '@trinity/chat';
import { ChatCustomFilter, ChatDateFilter, ChatFilterOperator,
  InteractionTypeEnum, InteractionTypesMap  } from '@trinity/chat';
import { toTitleCase } from '@trinity/chat-components/utils';

export function getInteractionTypes() {
  return [
    InteractionTypeEnum.CallCenterInteraction,
    InteractionTypeEnum.FbmsInteraction,
    InteractionTypeEnum.DocumentedInterest,
    InteractionTypeEnum.MedicalInsight,
  ].map(m => ({ id: m, name: InteractionTypesMap[m] }));
}

export function getPracticeSettings() {
  return [
    {
      id: '1',
      name: 'Option 1',
    },
    {
      id: '2',
      name: 'Option 2',
    },
  ];
}

enum Sentiment {
  Positive = 'positive',
  Negative = 'negative',
  Neutral = 'neutral',
}

export function getSentimentValues() {
  return [
    Sentiment.Positive,
    Sentiment.Neutral,
    Sentiment.Negative,
  ].map(m => ({ id: m, name: toTitleCase(m) }));
}

export function getCustomFilterOperators(type: ChatCustomFilter) {
  const operatorsMap: Record<ChatCustomFilter, ChatFilterOperator[]> = {
    [ChatCustomFilter.CallCenterSource]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.CallCenterSubSource]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InteractionDate]: [ChatFilterOperator.MoreRecentThan, ChatFilterOperator.PriorTo],
    [ChatCustomFilter.PracticeSetting]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InsightType]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.Product]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.Source]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.TherapeuticArea]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.TherapeuticSubArea]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.Sentiment]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InterestArea]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],

    [ChatCustomFilter.InteractionType]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InteractionSubType]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.InteractionDetail]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],
    [ChatCustomFilter.AgentFilter]: [ChatFilterOperator.Eq, ChatFilterOperator.Neq],

    [ChatCustomFilter.InteractionStartEndDate]: [ChatFilterOperator.MoreRecentThan, ChatFilterOperator.PriorTo],
  };

  return operatorsMap[type] || [];
}

export function getDefaultOperator(type: ChatCustomFilter) {
  return getCustomFilterOperators(type)[0];
}

const multiValueFilterTypes: Set<ChatCustomFilter> = new Set([
  ChatCustomFilter.PracticeSetting,
  ChatCustomFilter.InsightType,
  ChatCustomFilter.Product,
  ChatCustomFilter.Source,
  ChatCustomFilter.TherapeuticArea,
  ChatCustomFilter.TherapeuticSubArea,
  ChatCustomFilter.Sentiment,
  ChatCustomFilter.InterestArea,
  ChatCustomFilter.InteractionType,
  ChatCustomFilter.InteractionSubType,
  ChatCustomFilter.InteractionDetail,
  ChatCustomFilter.CallCenterSource,
  ChatCustomFilter.CallCenterSubSource,
  ChatCustomFilter.AgentFilter,
]);

export function getDefaultCustomFilterValue<T extends ChatCustomFilter>(type: T): CustomFilter.Item {
  if (type === ChatCustomFilter.InteractionDate) {
    return {
      type: ChatCustomFilter.InteractionDate,
      operator: ChatFilterOperator.MoreRecentThan,
      value: ChatDateFilter.Last30Days,
    };
  }

  if (multiValueFilterTypes.has(type)) {
    return {
      operator: ChatFilterOperator.Eq,
      value: [],
      type,
    } as CustomFilter.Item;
  }

  if (type === ChatCustomFilter.InteractionStartEndDate) {
    return {
      type: ChatCustomFilter.InteractionStartEndDate,
      operator: ChatFilterOperator.MoreRecentThan,
      value: null, // TODO: implement
    };
  }

  throw new UnreachableCaseError(type as never);
}

export function validateCustomFilterValue<T extends ChatCustomFilter>(value: CustomFilter.Item<T>) {
  if (value.type === ChatCustomFilter.InteractionDate) {
    return !!value.value;
  }

  if (multiValueFilterTypes.has(value.type)) {
    return !!(value.value as unknown[]).length;
  }

  if (value.type === ChatCustomFilter.InteractionStartEndDate) {
    return false; // TODO: implement
  }

  throw new UnreachableCaseError(value as never);
}
